
import React from 'react';
import { CalendarEvent } from '@/types/calendar';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { useDeleteEvent } from '@/hooks/useEvents';
import { AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

interface DeleteEventDialogProps {
  event: CalendarEvent;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const DeleteEventDialog: React.FC<DeleteEventDialogProps> = ({
  event,
  isOpen,
  onClose,
  onSuccess
}) => {
  const deleteEvent = useDeleteEvent();

  const handleDelete = async () => {
    try {
      await deleteEvent.mutateAsync(event.id);
      toast.success('Event deleted successfully!');
      onSuccess();
    } catch (error) {
      console.error('Error deleting event:', error);
      toast.error('Failed to delete event. Please try again.');
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="h-5 w-5" />
            Delete Event
          </DialogTitle>
          <DialogDescription>
            Are you sure you want to delete this event? This action cannot be undone.
          </DialogDescription>
        </DialogHeader>
        
        <div className="py-4">
          <div className="bg-muted p-4 rounded-md">
            <h4 className="font-medium">{event.title}</h4>
            <p className="text-sm text-muted-foreground mt-1">
              {new Date(event.start_time).toLocaleDateString()} at{' '}
              {new Date(event.start_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </p>
            {event.location && (
              <p className="text-sm text-muted-foreground">📍 {event.location}</p>
            )}
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button 
            variant="destructive" 
            onClick={handleDelete}
            disabled={deleteEvent.isPending}
          >
            {deleteEvent.isPending ? "Deleting..." : "Delete Event"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
